/**
 * Pricing — thin facade over fx.ts + currency.ts.
 *
 * Source of truth is `priceEUR`. TRY/USD are derived at display time using
 * rates from the WP admin (see src/lib/fx.ts). Locale only matters for
 * picking a *default* currency on first visit; a user-set cookie wins.
 */

import type { Product } from "./content/products";
import { defaultCurrencyForLocale, type Currency } from "./currency";
import { convertFromEur, formatPrice, type Rates } from "./fx";

export interface DisplayPrice {
  /** Numeric value in the chosen currency (post-rounding). 0 if unpriced. */
  amount: number;
  /** Source EUR amount (before conversion). 0 if unpriced. */
  eur: number;
  currency: Currency;
  symbol: string;
  /** Locale-formatted, e.g. "€16.90", "$19.95", "634,95 ₺". */
  formatted: string;
}

const SYMBOL: Record<Currency, string> = { EUR: "€", USD: "$", TRY: "₺" };

export function priceForProduct(
  product: Pick<Product, "priceEUR">,
  currency: Currency,
  rates: Rates
): DisplayPrice {
  const eur = product.priceEUR ?? 0;
  return {
    amount: convertFromEur(eur, currency, rates),
    eur,
    currency,
    symbol: SYMBOL[currency],
    formatted: formatPrice(eur, currency, rates),
  };
}

/**
 * @deprecated Use `priceForProduct(product, currency, rates)` instead.
 *   Retained because some server-rendered code still derives default
 *   currency from locale only — this preserves that behaviour with
 *   the *default* rates (no fetch). Prefer the explicit form.
 */
export function getPrice(
  product: Pick<Product, "priceEUR">,
  locale: string,
  rates?: Rates
): DisplayPrice {
  const currency = defaultCurrencyForLocale(locale);
  const safeRates: Rates = rates ?? { eurToTry: 37.5, eurToUsd: 1.17 };
  return priceForProduct(product, currency, safeRates);
}
